"use client";

import React from "react";
import { Drawer } from "vaul";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

type ModalSize = "auto" | "expanded" | "full";

interface DynamicModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  dismissible?: boolean;
  noAnimation?: boolean;
  /**
   * Controls the modal size:
   * - "auto" (default): Content-based height, bottom drawer with margins
   * - "expanded": Nearly full-screen but shows overlay at top (signals "still a modal")
   * - "full": Fills entire viewport (feels like a new screen)
   * @default "auto"
   */
  size?: ModalSize;
  /**
   * When `false` prevents vaul from repositioning inputs when the keyboard opens.
   * Set to `false` on iOS to prevent the drawer from being pushed out of frame.
   * @default true
   */
  repositionInputs?: boolean;
  /**
   * When `true`, don't move the drawer upwards if there's space, but rather
   * only change its height so it's fully scrollable when the keyboard is open.
   * Useful on iOS to keep the drawer in place.
   * @default false
   */
  fixed?: boolean;
  defaultOpen?: boolean;
}

/**
 * Dynamic modal component with size variants and smooth transitions.
 * Uses Vaul for the drawer behavior, with CSS transitions for size changes.
 */
const DynamicModal: React.FC<DynamicModalProps> = ({
  isOpen,
  onClose,
  children,
  dismissible = true,
  noAnimation = false,
  size = "auto",
  repositionInputs,
  fixed,
  defaultOpen = false,
}) => {
  const isFullScreen = size === "full";

  return (
    <div className={noAnimation ? "no-animation" : ""}>
      <Drawer.Root
        open={isOpen}
        onOpenChange={onClose}
        dismissible={dismissible}
        defaultOpen={defaultOpen}
        noBodyStyles={true}
        repositionInputs={repositionInputs}
        fixed={fixed}
      >
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/50 z-50" />
          {/* Drawer.Content is always full viewport - inner wrapper handles sizing */}
          <Drawer.Content className="fixed inset-0 z-50 outline-none flex flex-col justify-end pointer-events-none">
            <VisuallyHidden.Root>
              <Drawer.Title>Modal</Drawer.Title>
            </VisuallyHidden.Root>
            {/* Outer wrapper with margins - animates position */}
            <div
              className="pointer-events-auto flex flex-col transition-all duration-300 ease-out"
              style={{
                marginLeft: isFullScreen ? 0 : 16,
                marginRight: isFullScreen ? 0 : 16,
                marginBottom: isFullScreen
                  ? 0
                  : "calc(var(--felt-safe-area-bottom, 0px) + 16px)",
                // Use max-height for "auto", explicit height for others
                // max-height transitions work in CSS (unlike height: auto)
                height:
                  size === "full"
                    ? "100%"
                    : size === "expanded"
                      ? "calc(100dvh - var(--felt-safe-area-top, 44px) - 24px)"
                      : "auto",
                maxHeight:
                  size === "full"
                    ? "100%"
                    : size === "expanded"
                      ? "calc(100dvh - var(--felt-safe-area-top, 44px) - 24px)"
                      : "80vh",
              }}
            >
              {/* Card with border-radius animation */}
              <div
                className="bg-surface-elevated w-full overflow-hidden flex-1 flex flex-col transition-all duration-300 ease-out"
                style={{
                  borderRadius: isFullScreen ? 0 : 16,
                }}
              >
                {children}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
};

export default DynamicModal;
